import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BaseFormComponent } from '@classes/extends';
import { FicheclientService as FicheClientService, RFSurface, ReferencesService, Surface, SurfacesMillesime } from '@estel-numerique/galaclick-api';
import { TraductionCoreService, ValidatorPatternService } from '@estel-numerique/galaclick-core';
import { Constantes } from '@services/util/constantes';
import { FicheClientRfSurfaceComponent } from './ficheclient-rfsurface.component';
import { DialogGalaComponent } from '../../../../composants/dialog-gala/dialog-gala.component';
import { AddButton } from '../../../../composants/composants';
import { ButtonGalaDeleteComponent } from '../../../../composants/buttons/btn-gala-delete/btn-gala-delete.component';
import { InputGala } from '../../../../composants/inputs/input.component';
import { MatButton } from '@angular/material/button';

export interface FicheClientCampagnesComponentData {
    idClient: number;
    surfaces: SurfacesMillesime;
    rfSurfacesListe: RFSurface[];
    allRfSurfaces: RFSurface[]
}

@Component({
    templateUrl: 'ficheclient-campagnes.component.html',
    styleUrls: ['./ficheclient-campagnes.component.scss'],
    imports: [DialogGalaComponent, FormsModule, ReactiveFormsModule, AddButton, ButtonGalaDeleteComponent, InputGala, MatButton]
})
export class FicheClientCampagnesComponent extends BaseFormComponent implements OnInit {
    public traductionCore = inject(TraductionCoreService);
    public validPattern = inject(ValidatorPatternService);
    private formBuilder = inject(UntypedFormBuilder);
    private wsFicheClient = inject(FicheClientService);
    private wsReferences = inject(ReferencesService);
    private dialog = inject(MatDialog);
    private dialogRef = inject<MatDialogRef<FicheClientCampagnesComponent>>(MatDialogRef);
    public data = inject<FicheClientCampagnesComponentData>(MAT_DIALOG_DATA);

    public codeModule: string = Constantes.CodeOption.CFICHECLIENT; // Code module/option testé pour vérifier l'accès à cette fonction (si null = OK)

    public titre: string;
    @Output() saved = new EventEmitter<SurfacesMillesime>();

    // Traductions
    protected lgTrad: Record<string, string> = {};

    public surfaces: SurfacesMillesime = {};
    //public surfacesSave: SurfacesMillesime = {};
    public rfSurfacesListe: RFSurface[] = [];
    public listeRfSurfaces: RFSurface[];
    public rfSurfacesMillesime: RFSurface[];
    public idClient: number;

    constructor() {
        super();
        this.dialogRef.updateSize('1000px');
        this.lgTrad = {
            AFFSRFC: this.translate.instant('APPFICHECLIENT.AFFSRFC'),
            GRPTECHNIQUES: this.translate.instant("APPFICHECLIENT.COLTAB.GRPTECHNIQUES"),
            ENGAGEMENTS: this.translate.instant("APPFICHECLIENT.COLTAB.ENGAGEMENTS"),
            ATELIERLAIT: this.translate.instant('APPFICHECLIENT.COLTAB.ATELIERLAIT'),
            SURFACES: this.translate.instant('APPFICHECLIENT.SURFACES'),
            MODSURFACES: this.translate.instant('APPFICHECLIENT.MODSURFACES'),
            TYPE: this.translate.instant('TYPE'),
            TOTAL: this.translate.instant('TOTAL'),
            NOM: this.translate.instant("NOM"),
            ENR: this.translate.instant('ENR'),
            RET: this.translate.instant('RET'),
        };
        this.formData = this.formBuilder.group({
            FIRSTFORM: this.formBuilder.array([]),
        });
        this.show(this.data.idClient, this.data.surfaces, this.data.rfSurfacesListe, this.data.allRfSurfaces).catch(e => this.util.fErreur(e));
    }


    // =========================================================================
    // CHARGEMENT CONTEXTE
    // =========================================================================

    private async show(idClient: number, surfaces: SurfacesMillesime, rfSurfacesListe: RFSurface[], allRfSurfaces: RFSurface[]) {
        try {
            this.util.showLoader();

            this.idClient = idClient;
            this.surfaces = Object.assign({}, surfaces);
            //this.surfacesSave = Object.assign({}, surfaces);
            this.rfSurfacesListe = Object.assign([], rfSurfacesListe);
            this.rfSurfacesMillesime = await this.wsReferences.getRefSurfaceMillesime(this.surfaces.RFMILLESIME.IDRFMILLESIME).toPromise();
            this.chargerListeRfSurfaces(allRfSurfaces);

            this.chargerForm();
            this.formData.markAsPristine();
            this.formData.markAsUntouched();
        } catch (e) {
            this.util.fErreur(e);
        } finally {
            this.util.hideLoader();
        }
    }

    private chargerListeRfSurfaces(listeRf: RFSurface[]) {
        if (listeRf) {
            this.listeRfSurfaces = listeRf.filter((rf: RFSurface) => {
                let boOk = true;
                this.rfSurfacesListe.forEach((currentRf: RFSurface) => {
                    if (rf.IDRFSURFACE === currentRf.IDRFSURFACE) {
                        boOk = false;
                    }
                });
                return boOk;
            });
        }
    }

    async enregistrerSurface() {
        this.util.showLoader();
        try {
            this.validationFormulaire(this.formData);
            this.getResultatFromForm();

            if (this.formData.valid) {
                // on récupère la liste des surfaces qui auraient du être validé
                const lSurfaceParentARecalculer: Surface[] = this.checkParentSurfaceValorise();
                // si il n'y en a pas alors rien à faire
                if (lSurfaceParentARecalculer.length == 0) {
                    this.surfaces = await this.wsFicheClient.updateSurface(this.idClient, this.surfaces).toPromise();

                    this.saved.emit(this.surfaces);
                    this.hideModal();
                } else {
                    // on propose à l'utilisateur de valoriser automatiquement les surfaces non valorisées
                    this.util.fConfirm(this.translate.instant('APPFICHECLIENT.SURFACEPARENTERROR'), () => { this.calculerValeursParentsVide(lSurfaceParentARecalculer); });
                }
            }
        } catch (e) {
            this.util.fErreur(e);
        } finally {
            this.util.hideLoader();
        }
    }
    public checkRendement(index: number) {
        let rfSurface: RFSurface = null;
        let indexRf = 0;
        this.rfSurfacesListe.forEach((rf: RFSurface) => {
            if (indexRf == index) {
                rfSurface = rf;
            }
            indexRf++;
        });
        return rfSurface?.RENDEMENT == 1;
    }



    public checkRfSurface(index: number) {
        let rfSurface: RFSurface = null;
        let indexRf = 0;
        this.rfSurfacesListe.forEach((rf: RFSurface) => {
            if (indexRf == index) {
                rfSurface = rf;
            }
            indexRf++;
        });
        return rfSurface && this.rfSurfacesMillesime.find((rf: RFSurface) => { return rfSurface.IDRFSURFACE == rf.IDRFSURFACE; });
    }

    public checkHasChildren(rfSurface: RFSurface) {
        return this.rfSurfacesListe.find((rf: RFSurface) => { return rf.IDPARENT == rfSurface.IDRFSURFACE; });
    }

    // fonction qui pour chaque parent avec des sous lignes valorisée, vérfie que l'entete lui aussi contient une valeur un entete contient une sous ligne valorisée,
    // return la liste des surfaces non valorisés alors qu'elles le devraient
    public checkParentSurfaceValorise() {
        const lSurfaceParentARecalculer: Surface[] = [];
        this.surfaces.SURFACES.forEach((surf: Surface) => {
            if (surf.RFSURFACE?.IDPARENT != null && (surf.SURFLAIT != null || surf.SURFTOTAL != null)) {
                // on récupère la surface parent de l'élement du form
                const surfaceParent: RFSurface = this.rfSurfacesListe.find(RFSURFACE => RFSURFACE.IDRFSURFACE === surf.RFSURFACE.IDPARENT);
                // puis on verfie si la sous ligne est valorisé, que le parent l'est aussi dans le form
                if (surfaceParent != null) {
                    this.surfaces.SURFACES.forEach((surf2: Surface) => {
                        if (surf2.RFSURFACE?.IDRFSURFACE == surfaceParent.IDRFSURFACE) {
                            // le formulaire nous retourne des String a la place des nombres...
                            if ((((surf2.SURFLAIT == null) && (surf.SURFLAIT != null))
                                || ((surf2.SURFTOTAL == null) && (surf.SURFTOTAL != null)))) {
                                lSurfaceParentARecalculer.push(surf2);
                            }
                        }
                    });
                }
            }
        });
        return lSurfaceParentARecalculer;
    }

    // fonction qui recalcule une liste de surface;
    public calculerValeursParentsVide(lSurfaceParentARecalculer: Surface[]) {
        lSurfaceParentARecalculer.forEach((surf: Surface) => {
            this.calculerValeurs(surf.RFSURFACE);
        });
    }


    public calculerValeurs(rfSurface: RFSurface) {
        let indexRf = this.rfSurfacesListe.lastIndexOf(rfSurface);
        let index = 0;
        let formSurface: AbstractControl = null;
        this.firstForm.controls.forEach((curFormSurface: AbstractControl) => {
            if (index == indexRf) {
                formSurface = curFormSurface;
            }
            index++;
        });
        if (formSurface.value) {
            const surface: Surface = formSurface.value as Surface;
            surface.SURFLAIT = 0;
            surface.SURFTOTAL = 0;
            if (rfSurface.RENDEMENT == 1) {
                surface.RDTLAIT = 0;
                surface.RDTTOTAL = 0;
            }
            index = 0;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            this.firstForm.value.forEach((surf: Surface) => {
                indexRf = 0;
                this.rfSurfacesListe.forEach((rf: RFSurface) => {
                    if (index == indexRf) {
                        surf.RFSURFACE = rf;
                    }
                    indexRf++;
                });
                if (surf.RFSURFACE?.IDPARENT == rfSurface.IDRFSURFACE) {
                    surface.SURFLAIT += Number(surf.SURFLAIT);
                    surface.SURFTOTAL += Number(surf.SURFTOTAL);
                    if (rfSurface.RENDEMENT == 1) {
                        surface.RDTLAIT += Number(surf.RDTLAIT);
                        surface.RDTTOTAL += Number(surf.RDTTOTAL);
                    }
                }
                index++;
            });
            formSurface.get('SURFLAIT').setValue(surface.SURFLAIT);
            formSurface.get('SURFTOTAL').setValue(surface.SURFTOTAL);
            if (rfSurface.RENDEMENT == 1) {
                formSurface.get('RDTLAIT').setValue(surface.RDTLAIT);
                formSurface.get('RDTTOTAL').setValue(surface.RDTTOTAL);
            }
        }
    }

    public getResultatFromForm() {
        const listeTmpSrfc: Surface[] = [];
        //this.surfacesSave.SURFACES = [];
        let index = 0;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.firstForm.value.forEach((surface: Surface) => {
            //if (surface.SURFTOTAL > 0 || surface.RDTTOTAL > 0 || surface.SURFLAIT > 0 || surface.RDTLAIT > 0) {
            let rfSurface: RFSurface = null;
            let indexRf = 0;
            this.rfSurfacesListe.forEach((rf: RFSurface) => {
                if (indexRf == index) {
                    rfSurface = rf;
                }
                indexRf++;
            });

            surface = {
                IDSURFACECLIENT: surface.IDSURFACECLIENT,
                IDCLIENT: surface.IDCLIENT,
                RFSURFACE: rfSurface,
                RFMILLESIME: this.surfaces.RFMILLESIME,
                SURFTOTAL: (surface.SURFTOTAL > 0) ? surface.SURFTOTAL : null,
                RDTTOTAL: (surface.RDTTOTAL > 0) ? surface.RDTTOTAL : null,
                SURFLAIT: (surface.SURFLAIT > 0) ? surface.SURFLAIT : null,
                RDTLAIT: (surface.RDTLAIT > 0) ? surface.RDTLAIT : null,
            };
            listeTmpSrfc.push(surface);
            //}
            index++;
            //this.surfacesSave.SURFACES.push(surface);
        });
        this.surfaces.SURFACES = listeTmpSrfc;
    }

    public chargerForm() {
        this.firstForm.clear();
        this.surfaces.SURFACES.forEach((srfc: Surface) => {
            this.ajouterSurface(srfc);
        });
    }

    public ajouterSurface(surface: Surface) {
        const formSurface: UntypedFormGroup = this.formBuilder.group({
            IDSURFACECLIENT: surface.IDSURFACECLIENT,
            IDCLIENT: surface.IDCLIENT,
            RFSURFACE: surface.RFSURFACE,
            RFMILLESIME: surface.RFMILLESIME,
            SURFTOTAL: surface.SURFTOTAL,
            RDTTOTAL: surface.RDTTOTAL,
            SURFLAIT: surface.SURFLAIT,
            RDTLAIT: surface.RDTLAIT,
        });
        this.firstForm.push(formSurface);
    }


    public askSupprimerSurface(rf: RFSurface) {
        this.util.fConfirm(
            this.translate.instant('APPFICHECLIENT.CANSUPPRSRFC'),
            () => this.supprimerSurface(rf),
            this.translate.instant('APPFICHECLIENT.SUPPRSRFC'));
    }

    private supprimerSurface(rf: RFSurface) {
        const index: number = this.rfSurfacesListe.lastIndexOf(rf);
        if (index > -1) {
            this.rfSurfacesListe.splice(index, 1);
            this.surfaces.SURFACES.splice(index, 1);

            this.chargerForm();
        }
    }

    public isSurfaceSupprimable(rf: RFSurface) {
        let isSupprimable = true;
        this.rfSurfacesListe.forEach((rfSurface: RFSurface) => {
            if (rfSurface.IDPARENT == rf.IDRFSURFACE) {
                isSupprimable = false;
            }
        });
        return isSupprimable;
    }

    public checkTabulation(rfSurface: RFSurface) {
        if (rfSurface.IDPARENT == 0) {
            return 0;
        }
        const firstRf: RFSurface = this.rfSurfacesListe.find((rf: RFSurface) => {
            return rf.IDPARENT == 0;
        });
        if (firstRf) {
            if (rfSurface.IDPARENT == firstRf.IDRFSURFACE) {
                return 1;
            } else {
                let indentation = 3;
                this.rfSurfacesListe.forEach((rf: RFSurface) => {
                    if (rf.IDPARENT == firstRf.IDRFSURFACE) {
                        this.rfSurfacesListe.forEach((r: RFSurface) => {
                            if (r.IDPARENT == rf.IDRFSURFACE && r.IDRFSURFACE == rfSurface.IDRFSURFACE) {
                                indentation = 2;
                            }
                        });
                    }
                });
                return indentation;
            }
        }
        return 0;
    }

    public premiereSaisie(curFormSurface: AbstractControl) {
        this.formatNombreDecimals(curFormSurface);
        let isPremiereSasie = true;
        this.surfaces.SURFACES.forEach((surface: Surface) => {
            if (surface.SURFTOTAL > 0 || surface.SURFLAIT > 0 || surface.RDTTOTAL > 0 || surface.RDTLAIT > 0) {
                isPremiereSasie = false;
            }
        });
        if (isPremiereSasie) {
            curFormSurface.get('SURFLAIT').setValue(curFormSurface.get('SURFTOTAL').value);
            curFormSurface.get('RDTLAIT').setValue(curFormSurface.get('RDTTOTAL').value);
        }
    }

    public formatNombreDecimals(curFormSurface: AbstractControl) {
        if (curFormSurface.get('SURFTOTAL').value != null && (/^[0-9]{1,11}(\.[0-9]{3})+$/.exec((curFormSurface.get('SURFTOTAL').value as string)))) {
            curFormSurface.get('SURFTOTAL').setValue(parseFloat(curFormSurface.get('SURFTOTAL').value as string).toFixed(2));
        }
        if (curFormSurface.get('RDTTOTAL').value != null && (/^[0-9]{1,11}(\.[0-9]{3})+$/.exec((curFormSurface.get('RDTTOTAL').value as string)))) {
            curFormSurface.get('RDTTOTAL').setValue(parseFloat(curFormSurface.get('RDTTOTAL').value as string).toFixed(2));
        }
        if (curFormSurface.get('SURFLAIT').value != null && (/^[0-9]{1,11}(\.[0-9]{3})+$/.exec((curFormSurface.get('SURFLAIT').value as string)))) {
            curFormSurface.get('SURFLAIT').setValue(parseFloat(curFormSurface.get('SURFTOTAL').value as string).toFixed(2));
        }
        if (curFormSurface.get('RDTLAIT').value != null && (/^[0-9]{1,11}(\.[0-9]{3})+$/.exec((curFormSurface.get('RDTLAIT').value as string)))) {
            curFormSurface.get('RDTLAIT').setValue(parseFloat(curFormSurface.get('RDTLAIT').value as string).toFixed(2));
        }
    }

    public showModalRfSurface() {
        const dialogRfSurface = this.dialog.open(FicheClientRfSurfaceComponent, { data: this.listeRfSurfaces });
        dialogRfSurface.componentInstance.ajouter.subscribe((rfSurface) => {
            const listeTmpSrfc: Surface[] = [];
            this.rfSurfacesListe.push(rfSurface);
            if (this.rfSurfacesListe.length > 0) {
                this.rfSurfacesListe.sort((r1, r2) => { return r1.ORDREGLOBAL - r2.ORDREGLOBAL; });
            }
            this.chargerListeRfSurfaces(this.listeRfSurfaces);
            const srfc: Surface = {
                IDSURFACECLIENT: null,
                IDCLIENT: this.idClient,
                RFSURFACE: rfSurface,
                RFMILLESIME: this.surfaces.RFMILLESIME,
                SURFTOTAL: null,
                RDTTOTAL: null,
                SURFLAIT: null,
                RDTLAIT: null,
            };
            if (this.surfaces.SURFACES.length > 0) {
                this.rfSurfacesListe.forEach((rf: RFSurface) => {
                    const surface: Surface = this.surfaces.SURFACES.find((s: Surface) => {
                        return rf.IDRFSURFACE == s.RFSURFACE?.IDRFSURFACE;
                    });
                    if (surface) {
                        listeTmpSrfc.push(surface);
                    } else {
                        listeTmpSrfc.push(srfc);
                    }
                });
            } else {
                listeTmpSrfc.push(srfc);
            }
            this.surfaces.SURFACES = listeTmpSrfc;
            this.surfaces.SURFACES = Object.assign([], this.surfaces.SURFACES);

            this.chargerForm();
        });

    }

    get firstForm(): UntypedFormArray {
        return this.formData.get('FIRSTFORM') as UntypedFormArray;
    }

    ngOnInit() {
        super.ngOnInit();
    }

    askHideModal() {
        if (this.formData.untouched) {
            this.util.fConfirm(this.translate.instant('QUITNOSAVE'), () => {
                this.hideModal();
            }, this.translate.instant('MODIFDONE'));
        } else {
            this.hideModal();
        }
    }

    hideModal() {
        this.resetModalValue();
        this.dialogRef.close();
    }

    resetModalValue() {
        this.surfaces = {};
        //this.surfacesSave = {};
        this.rfSurfacesListe = [];
        this.formData.reset();
        this.formData.markAsPristine();
        this.formData.markAsUntouched();
    }

}
